// HitchKick Data Validation Utilities
// Filters out placeholders, breaks, and incomplete routines from HitchKick API data

/**
 * Validates if a HitchKick routine entry is a valid, complete routine for judging
 * @param {Object} routine - The routine object from HitchKick API
 * @returns {boolean} - True if the routine is valid for judging
 */
export const isValidRoutine = (routine) => {
  // Check if routine exists
  if (!routine) {
    console.log('❌ Routine validation failed: No routine data');
    return false;
  }

  // 1. Filter out non-routine types
  if (routine.type !== 'routine') {
    console.log(`❌ Routine validation failed: Type is "${routine.type}" (expected "routine")`, {
      id: routine.id,
      type: routine.type,
      number: routine.number
    });
    return false;
  }

  // 2. Must have a parentRoutine (this contains the actual routine data)
  if (!routine.parentRoutine) {
    console.log(`❌ Routine validation failed: No parentRoutine data`, {
      id: routine.id,
      number: routine.number,
      type: routine.type
    });
    return false;
  }

  // 3. Must have parentRoutineId (links to the actual routine)
  if (!routine.parentRoutineId) {
    console.log(`❌ Routine validation failed: No parentRoutineId`, {
      id: routine.id,
      number: routine.number,
      type: routine.type
    });
    return false;
  }

  // 4. Must have a routine title
  if (!routine.parentRoutine.title || routine.parentRoutine.title.trim() === '') {
    console.log(`❌ Routine validation failed: No routine title`, {
      id: routine.id,
      number: routine.number,
      parentRoutineId: routine.parentRoutineId
    });
    return false;
  }

  // 5. Must have submission routines (contains dancer and competition info)
  if (!routine.parentRoutine.submissionRoutines || 
      !Array.isArray(routine.parentRoutine.submissionRoutines) || 
      routine.parentRoutine.submissionRoutines.length === 0) {
    console.log(`❌ Routine validation failed: No submissionRoutines`, {
      id: routine.id,
      number: routine.number,
      title: routine.parentRoutine.title
    });
    return false;
  }

  const submissionRoutine = routine.parentRoutine.submissionRoutines[0];

  // 6. Must have routine dancers
  if (!submissionRoutine.routineDancers || 
      !Array.isArray(submissionRoutine.routineDancers) || 
      submissionRoutine.routineDancers.length === 0) {
    console.log(`❌ Routine validation failed: No routineDancers`, {
      id: routine.id,
      number: routine.number,
      title: routine.parentRoutine.title
    });
    return false;
  }

  // 7. Must have valid dancer information
  const firstDancer = submissionRoutine.routineDancers[0];
  if (!firstDancer.rosterDancers || 
      !firstDancer.rosterDancers.firstName || 
      !firstDancer.rosterDancers.lastName) {
    console.log(`❌ Routine validation failed: Invalid dancer information`, {
      id: routine.id,
      number: routine.number,
      title: routine.parentRoutine.title,
      dancerData: firstDancer.rosterDancers
    });
    return false;
  }

  // 8. Must have competition category information
  if (!submissionRoutine.category || !submissionRoutine.category.name) {
    console.log(`❌ Routine validation failed: No category information`, {
      id: routine.id,
      number: routine.number,
      title: routine.parentRoutine.title
    });
    return false;
  }

  // 9. Must have division information
  if (!submissionRoutine.division || !submissionRoutine.division.name) {
    console.log(`❌ Routine validation failed: No division information`, {
      id: routine.id,
      number: routine.number,
      title: routine.parentRoutine.title
    });
    return false;
  }

  // 10. Must have level information
  if (!submissionRoutine.level || !submissionRoutine.level.name) {
    console.log(`❌ Routine validation failed: No level information`, {
      id: routine.id,
      number: routine.number,
      title: routine.parentRoutine.title
    });
    return false;
  }

  // 11. Must have studio information
  if (!submissionRoutine.registrations || 
      !submissionRoutine.registrations.studios || 
      !submissionRoutine.registrations.studios.businessName) {
    console.log(`❌ Routine validation failed: No studio information`, {
      id: routine.id,
      number: routine.number,
      title: routine.parentRoutine.title
    });
    return false;
  }

  // 12. Must have a routine number (for display purposes)
  if (!routine.number && !routine.routineIndex) {
    console.log(`❌ Routine validation failed: No routine number or routineIndex`, {
      id: routine.id,
      title: routine.parentRoutine.title
    });
    return false;
  }

  // If all checks pass, this is a valid routine
  console.log(`✅ Routine validation passed`, {
    id: routine.id,
    number: routine.number,
    title: routine.parentRoutine.title,
    performer: `${firstDancer.rosterDancers.firstName} ${firstDancer.rosterDancers.lastName}`,
    studio: submissionRoutine.registrations.studios.businessName,
    category: submissionRoutine.category.name,
    division: submissionRoutine.division.name,
    level: submissionRoutine.level.name
  });

  return true;
};

/**
 * Filters an array of HitchKick routines to only include valid routines for judging
 * @param {Array} routines - Array of routine objects from HitchKick API
 * @returns {Array} - Filtered array containing only valid routines
 */
export const filterValidRoutines = (routines) => {
  if (!Array.isArray(routines)) {
    console.warn('filterValidRoutines: Input is not an array', routines);
    return [];
  }

  const originalCount = routines.length;
  const validRoutines = routines.filter(isValidRoutine);
  const filteredCount = validRoutines.length;
  const excludedCount = originalCount - filteredCount;

  console.log(`🔍 HitchKick Routine Filtering Results:`, {
    originalCount,
    validRoutines: filteredCount,
    excludedEntries: excludedCount,
    filteringRate: `${((excludedCount / originalCount) * 100).toFixed(1)}% excluded`
  });

  // Log some examples of what was excluded for debugging
  if (excludedCount > 0) {
    const excludedRoutines = routines.filter(routine => !isValidRoutine(routine));
    const excludedSample = excludedRoutines.slice(0, 3).map(routine => ({
      id: routine.id,
      type: routine.type,
      number: routine.number,
      routineIndex: routine.routineIndex,
      hasParentRoutine: !!routine.parentRoutine,
      parentRoutineId: routine.parentRoutineId,
      title: routine.parentRoutine?.title || 'No title',
      groupDisplayName: routine.group?.displayName || 'No group'
    }));

    console.log(`📋 Sample of excluded entries:`, excludedSample);
  }

  return validRoutines;
};

/**
 * Gets a summary of routine types in the data for debugging
 * @param {Array} routines - Array of routine objects from HitchKick API
 * @returns {Object} - Summary object with counts by type
 */
export const getRoutineTypeSummary = (routines) => {
  if (!Array.isArray(routines)) {
    return {};
  }

  const summary = routines.reduce((acc, routine) => {
    const type = routine.type || 'unknown';
    const hasParentRoutine = !!routine.parentRoutine;
    const key = `${type}${hasParentRoutine ? '_with_parent' : '_no_parent'}`;
    
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  console.log('📊 HitchKick Data Type Summary:', summary);
  return summary;
};

/**
 * Validates routine data and provides detailed feedback for debugging
 * @param {Object} routine - The routine object to validate
 * @returns {Object} - Validation result with details
 */
export const validateRoutineDetailed = (routine) => {
  const checks = {
    hasRoutine: !!routine,
    isRoutineType: routine?.type === 'routine',
    hasParentRoutine: !!routine?.parentRoutine,
    hasParentRoutineId: !!routine?.parentRoutineId,
    hasTitle: !!(routine?.parentRoutine?.title && routine.parentRoutine.title.trim()),
    hasSubmissionRoutines: !!(routine?.parentRoutine?.submissionRoutines && 
                             Array.isArray(routine.parentRoutine.submissionRoutines) && 
                             routine.parentRoutine.submissionRoutines.length > 0),
    hasRoutineDancers: false,
    hasDancerInfo: false,
    hasCategory: false,
    hasDivision: false,
    hasLevel: false,
    hasStudio: false,
    hasNumber: !!(routine?.number || routine?.routineIndex)
  };

  if (checks.hasSubmissionRoutines) {
    const submissionRoutine = routine.parentRoutine.submissionRoutines[0];
    checks.hasRoutineDancers = !!(submissionRoutine.routineDancers && 
                                 Array.isArray(submissionRoutine.routineDancers) && 
                                 submissionRoutine.routineDancers.length > 0);
    
    if (checks.hasRoutineDancers) {
      const firstDancer = submissionRoutine.routineDancers[0];
      checks.hasDancerInfo = !!(firstDancer.rosterDancers && 
                               firstDancer.rosterDancers.firstName && 
                               firstDancer.rosterDancers.lastName);
    }

    checks.hasCategory = !!(submissionRoutine.category && submissionRoutine.category.name);
    checks.hasDivision = !!(submissionRoutine.division && submissionRoutine.division.name);
    checks.hasLevel = !!(submissionRoutine.level && submissionRoutine.level.name);
    checks.hasStudio = !!(submissionRoutine.registrations && 
                         submissionRoutine.registrations.studios && 
                         submissionRoutine.registrations.studios.businessName);
  }

  const isValid = Object.values(checks).every(check => check === true);
  const failedChecks = Object.entries(checks).filter(([key, value]) => !value).map(([key]) => key);

  return {
    isValid,
    checks,
    failedChecks,
    routine: {
      id: routine?.id,
      type: routine?.type,
      number: routine?.number,
      routineIndex: routine?.routineIndex,
      title: routine?.parentRoutine?.title,
      parentRoutineId: routine?.parentRoutineId
    }
  };
};
